import { schema } from "@tellescope/schema";
import { ModelName } from "../../../../types";
import type { AccessEvaluator } from "../types";
import { CALENDAR_EVENTS_ACCESS_EVALUATOR } from "./calendar-events";
import { type CommonAccessConstraint, makeCommonAccessEvaluator } from "./common-evaluators";
import { ENDUSERS_ACCESS_EVALUATOR } from "./endusers";
import { USERS_ACCESS_EVALUATOR } from "./users";

export * from "./calendar-events";

/**
 * Models whose backend access rules the common evaluator cannot faithfully
 * reproduce from the schema alone, so {@link commonEvaluatorFor} must refuse to
 * build one for them — they require a bespoke evaluator:
 * - `users` → hardcoded by name (`_id === userId` for `Default`), no schema
 *   `access` constraint expresses it.
 * - `endusers` → the backend adds `accessTags` matching under `Default`/`Assigned`
 *   on top of the declared `assignedTo` filter; the schema filter alone
 *   under-grants.
 * - `user_notifications` / `database_records` → reserved proactively so a future
 *   registration can't silently route through the (partially-wrong) common path.
 */
const MODELS_WITH_NON_SCHEMA_ACCESS_RULES: ReadonlySet<ModelName> = new Set<ModelName>([
  "users",
  "endusers",
  "user_notifications",
  "database_records",
]);

/**
 * Builds the common evaluator for a model, binding the model's schema-declared
 * access constraints at construction. Reading from the schema (rather than
 * hardcoding) keeps the binding correct as constraints evolve and guarantees a
 * declared constraint is never silently dropped.
 *
 * Throws if the model declares a `dependency` constraint, or carries a backend
 * rule that isn't expressed as a schema constraint
 * ({@link MODELS_WITH_NON_SCHEMA_ACCESS_RULES}): both require a bespoke
 * evaluator, so routing one through the common evaluator is a bug that should
 * fail loudly at startup rather than silently mis-granting access.
 */
const commonEvaluatorFor = (model: keyof typeof schema & ModelName): AccessEvaluator<ModelName> => {
  if (MODELS_WITH_NON_SCHEMA_ACCESS_RULES.has(model)) {
    throw new Error(
      `Model "${model}" has backend access rules that are not expressed as schema constraints and cannot use the common access evaluator; register a bespoke evaluator instead.`
    );
  }

  const access = schema[model].constraints.access ?? [];

  const commonConstraints = access.reduce<CommonAccessConstraint[]>((acc, constraint) => {
    if (constraint.type === "creatorOnly") {
      acc.push({ type: "creatorOnly" });
    } else if (constraint.type === "filter" && typeof constraint.field === "string") {
      if (constraint.field.includes(".")) {
        throw new Error(
          `Model "${model}" declares a nested/dotted filter access field ("${constraint.field}") that the common access evaluator cannot read; register a bespoke evaluator instead.`
        );
      }
      acc.push({ type: "filter", field: constraint.field });
    } else if (constraint.type === "dependency") {
      throw new Error(
        `Model "${model}" declares a dependency access constraint and cannot use the common access evaluator; register a bespoke evaluator instead.`
      );
    }
    return acc;
  }, []);

  return makeCommonAccessEvaluator(commonConstraints);
};

const accessEvaluatorsByModel = {
  calendar_events: CALENDAR_EVENTS_ACCESS_EVALUATOR,
  users: USERS_ACCESS_EVALUATOR,
  endusers: ENDUSERS_ACCESS_EVALUATOR,
  table_views: commonEvaluatorFor("table_views"),
  appointment_locations: commonEvaluatorFor("appointment_locations"),
  calendar_event_templates: commonEvaluatorFor("calendar_event_templates"),
  enduser_observations: commonEvaluatorFor("enduser_observations"),
  appointment_booking_pages: commonEvaluatorFor("appointment_booking_pages"),
  organizations: commonEvaluatorFor("organizations"),
  tickets: commonEvaluatorFor("tickets"),
  ticket_queues: commonEvaluatorFor("ticket_queues"),
  ticket_threads: commonEvaluatorFor("ticket_threads"),
  inbox_threads: commonEvaluatorFor("inbox_threads"),
  call_hold_queues: commonEvaluatorFor("call_hold_queues"),
  emails: commonEvaluatorFor("emails"),
  chat_rooms: commonEvaluatorFor("chat_rooms"),
  sms_messages: commonEvaluatorFor("sms_messages"),
  // `chats` declares a `dependency` access constraint (access derives from the
  // parent chat_room via `roomId`), which the schema-driven common evaluator
  // cannot reproduce — so `commonEvaluatorFor` would throw. Every current
  // consumer performs a capability check (no record), where the evaluator never
  // runs; register a constraint-free common evaluator so the model is
  // permission-checkable. Replace with a dependency-aware bespoke evaluator
  // before introducing record-level `chats` checks.
  chats: makeCommonAccessEvaluator([]),
  templates: commonEvaluatorFor("templates"),
} as const satisfies Partial<{ [M in ModelName]: AccessEvaluator<M> }>;

/**
 * Union of model names that have a registered access evaluator. `usePermissionQuery`
 * and `usePermissionQueries` constrain their `model` parameter to this union.
 * Adding a new evaluator to {@link ACCESS_EVALUATORS} automatically widens it.
 */
export type ModelWithAccessEvaluator = keyof typeof accessEvaluatorsByModel;

/**
 * Registry of per-model permission evaluators.
 *
 * The mapped-type signature (rather than `typeof accessEvaluatorsByModel`)
 * ensures that `ACCESS_EVALUATORS[model]` distributes correctly when `model`
 * is a generic constrained to `ModelWithAccessEvaluator`, resolving to
 * `AccessEvaluator<Model>` instead of collapsing to the literal entry's type.
 */
export const ACCESS_EVALUATORS: { readonly [K in ModelWithAccessEvaluator]: AccessEvaluator<K> } =
  accessEvaluatorsByModel;
